import * as React from 'react';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import ReactTable, { CellInfo, Column } from 'react-table';
import { TextField, Checkbox, Label, DatePicker } from 'office-ui-fabric-react';

import { DateTimePickerService } from '@app/Services/DateTimePickerService';
import DateTimeService from '@app/Services/DateTimeService';

import { ApiUrls, Permissions } from '@app/AppConstants';
import ApiService from '@app/Services/ApiService';
import { securityService } from '@app/Services/SecurityService';
import { PersonContractViewModel, PersonImage, PersonInfoModel } from '@app/Models';

import { PersonImageBlock } from '@app/Pages/PersonInfo/Components/PersonImageBlock';
import { ChangeCheckboxCallback, ChangeDateFieldCallback, ValidateFormCallback } from '@app/Pages/PersonInfo/PersonInfoStore';
import { AzureAuth } from '@app/Components/AzureAuth';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { Loading } from '@app/Components/Loading';
import { TextCell } from '@app/Components/CellFormatters';

type UploadPersonImageCallback = (personId: string) => void;
type IsValidFieldCallback = (field: string) => boolean;

type PersonInfoBlockProps = {
    personInfo: PersonInfoModel;
    uploadPersonImageHandler: UploadPersonImageCallback;
    isValidField: IsValidFieldCallback;
    onChangeCheckboxHandler: ChangeCheckboxCallback;
    onChangeDateFieldHandler: ChangeDateFieldCallback;
    validateFormHandler: ValidateFormCallback;
};

@observer
export class PersonInfoBlock extends React.Component<PersonInfoBlockProps, {}> {
    private _store: ContactsBlockStore;

    constructor(props: PersonInfoBlockProps) {
        super(props);
        this._store = new ContactsBlockStore(props.personInfo.id);
    }

    componentDidUpdate(prevProps: Readonly<PersonInfoBlockProps>){
        if(prevProps.personInfo.id !== this.props.personInfo.id){
            this._store.personId = this.props.personInfo.id;
            this._store.loadPersonContacts(this._store.personId);
        }
    }

    render() {
        const { personInfo, uploadPersonImageHandler, isValidField } = this.props;
        if (!personInfo) return null;
        const { hasPermission, hasAnyPermission, permissionCss } = securityService;
        const { personContacts } = this._store;
        const canSeeContacts = hasPermission(Permissions.ViewObjectPersonContacts);

        return (
            <fieldset className="person-info-information inline-fields" style={{ height: '100%' }}>
                <legend>Person Information</legend>
                <div className="row">
                    <div className="col-7">
                        {hasPermission(Permissions.ViewPersonUsername) &&
                            <div className={'row' + permissionCss(Permissions.ViewPersonUsername, Permissions.EditPersonUsername)}>
                                <div className="col-xl-4 col-md-12">
                                    <Label>User Name</Label>
                                </div>
                                <div className="col-xl-8 col-md-12">
                                    <TextField
                                        maxLength={50}
                                        disabled={!hasPermission(Permissions.EditPersonUsername, !AzureAuth.isReadOnly)}
                                        onChange={(_, newValue) => personInfo.userName = newValue || ''}
                                        value={personInfo.userName || ''} />
                                    <Label />
                                </div>
                            </div>
                        }
                        {/* 
                         *
                          * Deactivated until EDW wants to make PersonData Master of these fields
                          *
                        <div className="row">
                            <div className="col-xl-4 col-md-4 d-flex align-items-center">
                                <Checkbox
                                    label="ELS qualified"
                                    onChange={this._onELSChange}
                                    checked={personInfo.elsQqualified}
                                    disabled={disabled} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xl-4 col-md-4 d-flex align-items-center">
                                <Checkbox
                                    label="APIS by EG"
                                    onChange={this._onAPISChange}
                                    checked={personInfo.apisByEg}
                                    disabled={disabled} />
                            </div>
                        </div>
                        */}
                    </div>
                    <div className="col-5">
                        {hasPermission(Permissions.ViewPersonPicture) &&
                            <div className={'row' + permissionCss(Permissions.ViewPersonPicture, Permissions.EditPersonPicture)}>
                                <div className="col-xl-3 col-md-12">
                                    <Label>Image</Label>
                                </div>
                                <div className="col-xl-9 col-md-12">
                                    <PersonImageBlock
                                        personId={personInfo.id}
                                        originalImageUrl={personInfo.originalImageUrl}
                                        personImages={personInfo.personImages}
                                        hasPersonImage={personInfo.hasPersonImage}
                                        onChangePersonImageHandler={uploadPersonImageHandler}
                                    />
                                </div>
                            </div>
                        }
                    </div>
                </div>
                <div className="row">
                    <div className="col-8">
                        {hasAnyPermission([Permissions.ViewPersonAeropersIsMember, Permissions.ViewPersonAeropersMemberFrom, Permissions.ViewPersonAeropersMemberTo]) &&
                            <div className="row">
                                <div className="col-3">
                                    <Label>Aeropers</Label>
                                </div>
                                <div className="col-4">
                                    <Label>Member From</Label>
                                </div>
                                <div className="col-4">
                                    <Label>Member Until</Label>
                                </div>
                            </div>
                        }
                        <div className="row">
                            {hasPermission(Permissions.ViewPersonAeropersIsMember) &&
                                <div className={'col-3' + permissionCss(Permissions.ViewPersonAeropersIsMember, Permissions.EditPersonAeropersIsMember)}>
                                    <Checkbox
                                        label="Is Member"
                                        onChange={this._onAeropersChangeChange}
                                        checked={personInfo.isAeropersMember}
                                        disabled={!hasPermission(Permissions.EditPersonAeropersIsMember, !AzureAuth.isReadOnly)}
                                    />
                                </div>
                            }
                            {hasPermission(Permissions.ViewPersonAeropersMemberFrom) &&
                                <div className={'col-4' + permissionCss(Permissions.ViewPersonAeropersMemberFrom, Permissions.EditPersonAeropersMemberFrom)}>
                                    <DatePicker
                                        className="date-field"
                                        allowTextInput={hasPermission(Permissions.EditPersonAeropersMemberFrom, !AzureAuth.isReadOnly)}
                                        calloutProps={{ hidden: !hasPermission(Permissions.EditPersonAeropersMemberFrom, !AzureAuth.isReadOnly) }}
                                        formatDate={DateTimePickerService.onFormatDate}
                                        parseDateFromString={DateTimePickerService.onParseDateFromString}
                                        onSelectDate={this._onAeropersMemberFromChange}
                                        value={personInfo.aeropersMemberFromDate || undefined}
                                        disabled={!hasPermission(Permissions.EditPersonAeropersMemberFrom, !AzureAuth.isReadOnly)}
                                    />

                                    {!isValidField('aeropersMemberFromDate') &&
                                        <p className="ms-TextField-errorMessage errorMessage">Date From must be before date To</p>
                                    }
                                </div>
                            }
                            {hasPermission(Permissions.ViewPersonAeropersMemberTo) &&
                                <div className={'col-4' + permissionCss(Permissions.ViewPersonAeropersMemberTo, Permissions.EditPersonAeropersMemberTo)}>
                                    <DatePicker
                                        className="date-field"
                                        allowTextInput={hasPermission(Permissions.EditPersonAeropersMemberTo, !AzureAuth.isReadOnly)}
                                        calloutProps={{ hidden: !hasPermission(Permissions.EditPersonAeropersMemberTo, !AzureAuth.isReadOnly) }}
                                        formatDate={DateTimePickerService.onFormatDate}
                                        parseDateFromString={DateTimePickerService.onParseDateFromString}
                                        onSelectDate={this._onAeropersMemberUntilChange}
                                        value={personInfo.aeropersMemberUntilDate || undefined}
                                        disabled={!hasPermission(Permissions.EditPersonAeropersMemberTo, !AzureAuth.isReadOnly)}
                                    />
                                    {!isValidField('aeropersMemberUntilDate') &&
                                        <p className="ms-TextField-errorMessage errorMessage">Date To must be after date From</p>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                    {hasPermission(Permissions.ViewPersonPictureOriginalDerivations) &&
                        <div className={'col-4' + permissionCss(Permissions.ViewPersonPictureOriginalDerivations)}>
                            {(personInfo.personImages && personInfo.personImages.length > 0) &&
                                this.renderPersonImageDerivations(personInfo.personImages, personInfo.id, personInfo.originalImageUrl)
                            }
                        </div>
                    }
                </div>
                {canSeeContacts &&
                    <>
                        <Loading loading={this._store.personContactsLoader.isPending} />
                        <div className="table-label">
                            <Label>Contacts</Label>
                        </div>
                        {personContacts && <ReactTable<PersonContractViewModel>
                            data={personContacts}
                            columns={this._personContactColumns}
                            pageSize={personContacts.length}
                            showPageSizeOptions={false}
                            showPagination={false}
                            className={'ReactTable-fixed-height' + permissionCss(Permissions.ViewObjectPersonContacts)}
                        />}
                    </>
                }
            </fieldset>
        );
    }

    renderPersonImageDerivations(personImages: PersonImage[], personId: string, originalImageUrl?: string) {
        const imageDerivations: JSX.Element[] = [];
        personImages.forEach(element => {
            imageDerivations.push(<div key={element.name} className="person-image-deriviation-item"><a target="_blank" href={element.url} rel="noreferrer">{element.name}</a></div>);
        });
        return (
            <>
                {originalImageUrl &&
                    <div className="person-image-original">
                        <a target="_blank" href={originalImageUrl} rel="noreferrer">Original Image</a>
                    </div>
                }
                <h6>Image-Derivations</h6>
                {imageDerivations}
            </>
        );
    }

    /*private _onELSChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        if (this.props.isReadOnly) return;
        this.props.onChangeCheckboxHandler('elsQqualified', !!checked);
    };

    private _onAPISChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        if (this.props.isReadOnly) return;
        this.props.onChangeCheckboxHandler('apisByEg', !!checked);
    };*/

    private _onAeropersChangeChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        this.props.onChangeCheckboxHandler('isAeropersMember', !!checked);
        if (!checked) {
            this.props.onChangeDateFieldHandler('aeropersMemberFromDate', undefined);
            this.props.onChangeDateFieldHandler('aeropersMemberUntilDate', undefined);
        }
    };

    private _onAeropersMemberFromChange = (date: Date | null | undefined): void => {
        this.props.personInfo.aeropersMemberFromDate = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined;
        const isValid = (!date && !this.props.personInfo.aeropersMemberUntilDate) || (!!date && DateTimeService.isValidDate(date) &&
            (!this.props.personInfo.aeropersMemberUntilDate || DateTimeService.isBeforeDate(date, this.props.personInfo.aeropersMemberUntilDate)));
        this.props.validateFormHandler('aeropersMemberFromDate', isValid);
        if (this.props.personInfo.aeropersMemberUntilDate && this.props.personInfo.aeropersMemberFromDate) {
            const isValidAeropersMemberUntilDate = DateTimeService.isAfterDate(this.props.personInfo.aeropersMemberUntilDate, this.props.personInfo.aeropersMemberFromDate);
            this.props.validateFormHandler('aeropersMemberUntilDate', isValidAeropersMemberUntilDate);
        }

    };

    private _onAeropersMemberUntilChange = (date: Date | null | undefined): void => {
        this.props.personInfo.aeropersMemberUntilDate = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : undefined;
        const isValid = !date || (!!date && DateTimeService.isValidDate(date) &&
            (!!this.props.personInfo.aeropersMemberFromDate && DateTimeService.isAfterDate(date, this.props.personInfo.aeropersMemberFromDate)));

        this.props.validateFormHandler('aeropersMemberUntilDate', isValid);
    };

    private _renderTextCell = (cellInfo: CellInfo) => {
        return <TextCell value={cellInfo.value} />;
    };

    private _personContactColumns: Column<PersonContractViewModel>[] = [
        {
            Header: 'Type',
            accessor: 'type',
            Cell: this._renderTextCell,
            show: securityService.hasPermission(Permissions.ViewObjectPersonContacts),
            className: securityService.permissionCss(Permissions.ViewObjectPersonContacts),
            minWidth: 150
        },
        {
            Header: 'Value',
            accessor: 'value',
            Cell: this._renderTextCell,
            show: securityService.hasPermission(Permissions.ViewObjectPersonContacts),
            className: securityService.permissionCss(Permissions.ViewObjectPersonContacts),
            minWidth: 300
        },
        {
            Header: 'Name',
            accessor: 'name',
            Cell: this._renderTextCell,
            show: securityService.hasPermission(Permissions.ViewObjectPersonContacts),
            className: securityService.permissionCss(Permissions.ViewObjectPersonContacts),
            minWidth: 150
        }
    ];
}

class ContactsBlockStore {
    @observable personContacts: PersonContractViewModel[];
    @observable personId: string;
    public personContactsLoader: PromiseCompletion = new PromiseCompletion();

    constructor(personId: string) {
        this.personId = personId;
        this.loadPersonContacts(personId);
    }

    @action.bound
    async loadPersonContacts(selectedPersonId: string) {
        if (!securityService.hasPermission(Permissions.ViewObjectPersonContacts))
            return;
        const params = { personId: selectedPersonId };
        const { data: personContacts } = await ApiService.getTypedData<PersonContractViewModel[]>(ApiUrls.PersonContactsUrl, params, { completion: this.personContactsLoader });
        this.personContacts = personContacts;
    }
}